/**
 * CreateLgpdRequestModal — Item 362 · registro manual de solicitação LGPD.
 *
 * Caso de uso: o titular pede direitos por canal externo (telefone, e-mail,
 * balcão) e o DPO registra a solicitação na fila com o prazo legal correndo
 * (o Engine calcula `legalDeadlineAt` ao criar; status inicial `received`).
 *
 * Contrato real (R15 · POST /v1/lgpd/requests · cap `lgpd-requests-create`):
 *   - requestType: 6 valores EN canônicos (labels PT via REQUEST_TYPE_LABEL);
 *   - requesterPersonId OU requesterSelfDeclaredData (titular ainda não
 *     vinculado — a vinculação/validação acontece no passo de validação de
 *     identidade do workflow ADR-098);
 *   - requestDetails é OBJETO livre → { canal, descricao } (legível no drawer).
 *
 * Busca de titular é GATEADA em `person-read` (fetch só dispara com a cap —
 * o papel DPO hoje NÃO a tem; para ele o caminho é auto-declarado, que é
 * exatamente o que o backend modela para entrada manual).
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { useCreateLgpdRequest } from '../../../hooks/use-lgpd-requests';
import { api } from '../../../lib/api-client';

import { REQUEST_TYPE_LABEL } from './LgpdRequestStatusBadge';

import type { LgpdRequestType } from './types';

const REQUEST_TYPES: readonly LgpdRequestType[] = [
  'access',
  'correction',
  'anonymization',
  'portability',
  'opposition',
  'consent_withdrawal',
];

const CHANNEL_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'telefone', label: 'Telefone' },
  { value: 'email', label: 'E-mail' },
  { value: 'presencial', label: 'Presencial / balcão' },
  { value: 'carta', label: 'Carta' },
  { value: 'outro', label: 'Outro' },
];

interface CreateLgpdRequestModalProps {
  onClose: () => void;
  /** Chamado após criação bem-sucedida (a lista já foi invalidada pelo hook). */
  onCreated: () => void;
}

export function CreateLgpdRequestModal({
  onClose,
  onCreated,
}: CreateLgpdRequestModalProps): JSX.Element {
  const canSearchPersons = useCapability('person-read');
  const create = useCreateLgpdRequest();

  const [requestType, setRequestType] = useState<LgpdRequestType>('access');
  const [personQuery, setPersonQuery] = useState('');
  const [selectedPerson, setSelectedPerson] = useState<{ id: string; name: string } | null>(null);
  const [name, setName] = useState('');
  const [cpf, setCpf] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [channel, setChannel] = useState('telefone');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Busca de titular — fetch GATEADO na cap (não só o render).
  const trimmedQuery = personQuery.trim();
  const personSearch = useQuery({
    queryKey: ['lgpd-create-person-search', trimmedQuery],
    queryFn: () => api.listPersons({ q: trimmedQuery, limit: 8 }),
    enabled: canSearchPersons && selectedPerson === null && trimmedQuery.length >= 2,
    staleTime: 30_000,
  });

  const isValid = selectedPerson !== null || name.trim() !== '';

  function handleSubmit(): void {
    if (!isValid || create.isPending) return;
    setError(null);

    const selfDeclared: Record<string, string> = {};
    if (name.trim() !== '') selfDeclared.name = name.trim();
    if (cpf.trim() !== '') selfDeclared.cpf = cpf.trim();
    if (email.trim() !== '') selfDeclared.email = email.trim();
    if (phone.trim() !== '') selfDeclared.phone = phone.trim();

    const details: Record<string, string> = { canal: channel };
    if (description.trim() !== '') details.descricao = description.trim();

    create.mutate(
      {
        requestType,
        ...(selectedPerson !== null && { requesterPersonId: selectedPerson.id }),
        ...(Object.keys(selfDeclared).length > 0 && { requesterSelfDeclaredData: selfDeclared }),
        requestDetails: details,
      },
      {
        onSuccess: () => {
          onCreated();
        },
        onError: (err) => {
          setError(
            err.message !== ''
              ? err.message
              : 'Não foi possível registrar a solicitação. Tente novamente.',
          );
        },
      },
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Nova solicitação LGPD"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(17, 24, 39, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 70,
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: 12,
          width: 560,
          maxWidth: 'calc(100vw - 48px)',
          maxHeight: 'calc(100vh - 64px)',
          overflowY: 'auto',
          padding: 24,
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
      >
        <h2 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0 }}>
          Nova solicitação LGPD
        </h2>
        <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 0 16px' }}>
          Registro manual de pedido recebido por canal externo. O prazo legal (15 dias corridos)
          começa a contar do registro; a validação de identidade do titular acontece na fila.
        </p>

        <Field label="Tipo de solicitação" htmlFor="lgpd-new-type">
          <select
            id="lgpd-new-type"
            style={inputStyle}
            value={requestType}
            onChange={(e) => setRequestType(e.target.value as LgpdRequestType)}
          >
            {REQUEST_TYPES.map((t) => (
              <option key={t} value={t}>
                {REQUEST_TYPE_LABEL[t] ?? t}
              </option>
            ))}
          </select>
        </Field>

        {/* Titular vinculado — só para quem pode consultar o cadastro de pessoas */}
        {canSearchPersons && (
          <Field label="Titular já cadastrado (opcional)" htmlFor="lgpd-new-person">
            {selectedPerson !== null ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 10px',
                  borderRadius: 8,
                  backgroundColor: '#eef2ff',
                  fontSize: 13,
                }}
              >
                <span style={{ fontWeight: 600 }}>{selectedPerson.name}</span>
                <button
                  type="button"
                  style={{ ...linkButtonStyle }}
                  onClick={() => setSelectedPerson(null)}
                >
                  remover vínculo
                </button>
              </div>
            ) : (
              <>
                <input
                  id="lgpd-new-person"
                  style={inputStyle}
                  placeholder="Buscar pessoa por nome, e-mail ou telefone…"
                  value={personQuery}
                  onChange={(e) => setPersonQuery(e.target.value)}
                />
                {personSearch.data !== undefined && trimmedQuery.length >= 2 && (
                  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {personSearch.data.data.length === 0 ? (
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>
                        Nenhuma pessoa encontrada — registre com os dados auto-declarados abaixo.
                      </span>
                    ) : (
                      personSearch.data.data.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          style={{
                            textAlign: 'left',
                            padding: '6px 10px',
                            borderRadius: 6,
                            border: '1px solid #e5e7eb',
                            backgroundColor: '#fff',
                            fontSize: 13,
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            setSelectedPerson({ id: p.id, name: p.displayName });
                            setPersonQuery('');
                          }}
                        >
                          {p.displayName}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </Field>
        )}

        {/* Dados auto-declarados — caminho padrão do registro por canal externo */}
        {selectedPerson === null && (
          <>
            <Field label="Nome do titular *" htmlFor="lgpd-new-name">
              <input
                id="lgpd-new-name"
                style={inputStyle}
                placeholder="Como o titular se identificou"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="CPF (declarado)" htmlFor="lgpd-new-cpf">
                <input
                  id="lgpd-new-cpf"
                  style={inputStyle}
                  placeholder="000.000.000-00"
                  value={cpf}
                  onChange={(e) => setCpf(e.target.value)}
                />
              </Field>
              <Field label="Telefone" htmlFor="lgpd-new-phone">
                <input
                  id="lgpd-new-phone"
                  style={inputStyle}
                  placeholder="(51) 9…"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </Field>
            </div>
            <Field label="E-mail" htmlFor="lgpd-new-email">
              <input
                id="lgpd-new-email"
                style={inputStyle}
                placeholder="titular@exemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
          </>
        )}

        <Field label="Canal de entrada" htmlFor="lgpd-new-channel">
          <select
            id="lgpd-new-channel"
            style={inputStyle}
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
          >
            {CHANNEL_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Descrição do pedido" htmlFor="lgpd-new-description">
          <textarea
            id="lgpd-new-description"
            style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
            placeholder="O que o titular pediu, com as palavras dele."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        {error !== null && (
          <p
            role="alert"
            style={{
              fontSize: 12,
              color: '#b91c1c',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 8,
              padding: '8px 10px',
              margin: '0 0 12px',
            }}
          >
            {error}
          </p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              backgroundColor: '#fff',
              color: '#374151',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancelar
          </button>
          <button
            type="button"
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: 'none',
              backgroundColor: isValid && !create.isPending ? '#4f46e5' : '#c7d2fe',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: isValid && !create.isPending ? 'pointer' : 'not-allowed',
            }}
            disabled={!isValid || create.isPending}
            onClick={handleSubmit}
          >
            {create.isPending ? 'Registrando…' : 'Registrar solicitação'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── UI helpers (inline styles — padrão da feature centro-direitos) ──────────

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div style={{ marginBottom: 12 }}>
      <label
        htmlFor={htmlFor}
        style={{
          display: 'block',
          fontSize: 12,
          fontWeight: 600,
          color: '#374151',
          marginBottom: 4,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  fontSize: 13,
  color: '#111827',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
};

const linkButtonStyle: React.CSSProperties = {
  border: 'none',
  background: 'none',
  color: '#4f46e5',
  fontSize: 12,
  cursor: 'pointer',
  padding: 0,
};
